import React, { useState, useEffect } from 'react';
import { Typography, Grid, Chip } from '@mui/joy';
import { Card, Button, Table, Alert } from '@components/ui';
import { securityService, SecurityScan, ScanResults } from '@services/security.service';
import {
  ShieldCheckIcon,
  PlayIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const auditTypes = [
  {
    id: 'network',
    name: 'Network Security Audit',
    description: 'Comprehensive network infrastructure security assessment',
    duration: '2-4 hours',
    icon: ShieldCheckIcon,
  },
  {
    id: 'application',
    name: 'Application Security Audit',
    description: 'Web application security testing and code analysis',
    duration: '3-6 hours',
    icon: DocumentTextIcon,
  },
  {
    id: 'infrastructure',
    name: 'Infrastructure Audit',
    description: 'Server and cloud infrastructure security review',
    duration: '1-3 hours',
    icon: ExclamationTriangleIcon,
  },
  {
    id: 'compliance',
    name: 'Compliance Audit',
    description: 'Regulatory compliance assessment (PCI-DSS, HIPAA, GDPR)',
    duration: '2-5 hours',
    icon: ClockIcon,
  },
];

export const SecurityAudit: React.FC = () => {
  const [activeScans, setActiveScans] = useState<SecurityScan[]>([]);
  const [completedScans, setCompletedScans] = useState<SecurityScan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const scans = await securityService.getScans({ type: 'audit' });
      setActiveScans(scans.filter(scan => ['pending', 'in-progress'].includes(scan.status)));
      setCompletedScans(scans.filter(scan => ['completed', 'failed'].includes(scan.status)));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch security scans');
    }
  };

  const startAudit = async (auditType: string) => {
    setLoading(true);
    setError(null);

    try {
      const scope = {
        includeNetwork: auditType === 'network' || auditType === 'all',
        includeApplications: auditType === 'application' || auditType === 'all',
        includeInfrastructure: auditType === 'infrastructure' || auditType === 'all',
        includeCompliance: auditType === 'compliance' || auditType === 'all',
      };

      await securityService.startSecurityAudit(scope);
      await fetchScans();
    } catch (err: any) {
      setError(err.message || 'Failed to start security audit');
    } finally {
      setLoading(false);
    }
  };

  const scanColumns = [
    { key: 'id', header: 'Scan ID', width: '15%' },
    { 
      key: 'type', 
      header: 'Type', 
      width: '20%',
      render: (value: string) => (
        <Chip size="sm" color="primary" variant="soft">
          {value.charAt(0).toUpperCase() + value.slice(1)}
        </Chip>
      )
    },
    { 
      key: 'status', 
      header: 'Status', 
      width: '15%',
      render: (value: string) => {
        const colors: Record<string, any> = {
          pending: 'warning',
          'in-progress': 'primary',
          completed: 'success',
          failed: 'danger'
        };
        return (
          <Chip size="sm" color={colors[value] || 'neutral'} variant="soft">
            {value}
          </Chip>
        );
      }
    },
    { 
      key: 'startTime', 
      header: 'Started', 
      width: '20%',
      render: (value: Date) => new Date(value).toLocaleString()
    },
    { 
      key: 'endTime', 
      header: 'Completed', 
      width: '20%',
      render: (value: Date | undefined) => 
        value ? new Date(value).toLocaleString() : 'In Progress'
    },
    { 
      key: 'results', 
      header: 'Vulnerabilities', 
      width: '10%',
      render: (value: ScanResults | undefined, row: SecurityScan) => {
        if (!value || row.status !== 'completed') return '-';
        const criticalCount = value.criticalCount || 0;
        const highCount = value.highCount || 0;
        return (
          <span className={criticalCount > 0 ? 'text-red-600 font-semibold' : 
                          highCount > 0 ? 'text-orange-600 font-semibold' : 
                          'text-green-600'}>
            {value.totalVulnerabilities}
          </span>
        );
      }
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Typography level="h2" className="font-bold text-gray-900 dark:text-white">
          Security Audit
        </Typography>
        <Typography level="body-md" className="text-gray-600 dark:text-gray-400 mt-1">
          Comprehensive security assessments and compliance audits
        </Typography>
      </div>

      {error && (
        <Alert color="danger" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Audit Types */}
      <Grid container spacing={3}>
        {auditTypes.map((auditType) => {
          const Icon = auditType.icon;
          return (
            <Grid key={auditType.id} xs={12} md={6}>
              <Card className="p-6 h-full">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg">
                      <Icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <Typography level="h4" className="font-semibold">
                        {auditType.name}
                      </Typography>
                      <Typography level="body-sm" className="text-gray-500">
                        Duration: {auditType.duration}
                      </Typography>
                    </div>
                  </div>
                </div>
                
                <Typography level="body-md" className="text-gray-600 dark:text-gray-400 mb-4">
                  {auditType.description}
                </Typography>
                
                <Button
                  startDecorator={<PlayIcon className="w-4 h-4" />}
                  onClick={() => startAudit(auditType.id)}
                  loading={loading}
                  fullWidth
                  variant="solid"
                >
                  Start Audit
                </Button>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Comprehensive Audit */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
        <div className="flex items-center justify-between">
          <div>
            <Typography level="h4" className="font-semibold mb-2">
              Comprehensive Security Audit
            </Typography>
            <Typography level="body-md" className="text-gray-600 dark:text-gray-400">
              Run all security audits simultaneously for complete coverage
            </Typography>
          </div>
          <Button
            size="lg"
            startDecorator={<PlayIcon className="w-5 h-5" />}
            onClick={() => startAudit('all')}
            loading={loading}
            variant="solid"
            color="primary"
          >
            Start Full Audit
          </Button>
        </div>
      </Card>

      {/* Active Scans */}
      {activeScans.length > 0 && (
        <Card className="p-6">
          <Typography level="h4" className="font-semibold mb-4">
            Active Scans
          </Typography>
          <Table
            columns={scanColumns}
            data={activeScans}
            striped
            loading={loading}
          />
        </Card>
      )}

      {/* Completed Scans */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <Typography level="h4" className="font-semibold">
            Recent Audits
          </Typography>
          <Button size="sm" variant="soft" onClick={fetchScans}>
            Refresh
          </Button>
        </div>
        
        <Table
          columns={scanColumns}
          data={completedScans}
          striped
          emptyMessage="No completed audits found"
        />
      </Card>
    </div>
  );
};